import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../config/firebaseConfig";
import { useAuthState } from "react-firebase-hooks/auth";
import { LoadingDots } from "../components";
import { useSelector } from "react-redux";

export const isUnverifiedEmailUser = (user) => {
  if (!user) return false;
  if (user.emailVerified) return false;
  const isPasswordProvider = user.providerData?.some(
    (provider) => provider.providerId === "password"
  );
  const isEmailOnly = Boolean(user.email && !user.phoneNumber);
  return (isPasswordProvider || isEmailOnly) && !user.emailVerified;
};

const UserAuthProtected = ({ children, authenticate = true }) => {
  const [userAuth, authLoading] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, loading: userDetailsLoading } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (!authLoading) {
      if (authenticate) {
        // Protected routes (Dashboard, etc.)
        if (!userAuth) {
          navigate("/login");
        } else if (isUnverifiedEmailUser(userAuth)) {
          // Email/password user must verify email before accessing dashboard
          navigate("/register/email");
        }
      } else if (!authenticate && userAuth) {
        // Guest routes (/login, /register)
        if (isUnverifiedEmailUser(userAuth)) {
          // Do NOT redirect unverified email user to dashboard.
          // Keep them on email auth view so they can see the verification sent screen.
          if (!location.pathname.includes("/email")) {
            navigate("/register/email");
          }
          return;
        }

        if (userDetailsLoading) {
          // Waiting for user details
        } else {
          const searchParams = new URLSearchParams(location.search);
          const isAiLab = searchParams.get("source") === "ai-lab";
          const demoType = searchParams.get("type");

          let targetDashboard = "/dashboard";
          if (demoType) {
            targetDashboard = `/dashboard${location.search}`;
          } else if (isAiLab) {
            targetDashboard = "/dashboard?offer=ai-lab";
          }

          if (userDetails?.accountType === "school") {
            navigate("/schoolDashboard");
          } else {
            navigate(targetDashboard);
          }
        }
      }
    }
  }, [
    authenticate,
    navigate,
    authLoading,
    userAuth,
    userDetails,
    userDetailsLoading,
    location.search,
    location.pathname,
  ]);

  if (authLoading) return <LoadingDots />;
  if (authenticate) {
    if (!userAuth || isUnverifiedEmailUser(userAuth)) {
      return <LoadingDots />;
    }
    if (userDetailsLoading) {
      return <LoadingDots />;
    }
  }

  return children;
};

export default UserAuthProtected;
